import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSession } from '../../stores/session';
import { playlists, type Playlist } from './api';

/**
 * One playlist, and everything that can be done to it.
 *
 * ## Why this is a hook and not a component's private state
 *
 * Two surfaces edit the same playlist: the panel inside the library dock, and
 * the page it has at `/playlists/:id`. Both reorder, rename, remove tracks,
 * change who can see it and delete it - and a second copy of that is five
 * chances for the two to disagree about what "remove" does, or for one of them
 * to forget to invalidate the list the other is showing.
 *
 * The presentation is genuinely different - a narrow dock panel is not a page -
 * so the components stay separate. The behaviour is identical, so it lives
 * here.
 *
 * ## The invalidation is the interesting part
 *
 * Every mutation refreshes two keys: this playlist, and the owner's list of
 * playlists. Renaming without the second leaves the old title in the dock, the
 * rail and the page you did not happen to be looking at - which is how a rename
 * comes to look like it silently failed.
 */
export function usePlaylist(playlistId: number) {
  const client = useQueryClient();
  const user = useSession((state) => state.user);

  const key = ['playlist', playlistId];

  const query = useQuery({
    queryKey: key,
    queryFn: () => playlists.get(playlistId),
  });

  const refresh = () => {
    void client.invalidateQueries({ queryKey: key });
    void client.invalidateQueries({ queryKey: ['playlists', user?.username] });
  };

  return {
    ...query,
    tracks: query.data?.tracks ?? [],
    /*
     * Whether the reader may edit, as the server decided.
     *
     * `is_owner` off the response rather than comparing ids here - the server
     * is the one enforcing it, and a second rule in the client is a second rule
     * that can disagree.
     */
    mine: query.data?.is_owner ?? false,
    /*
     * Contents, not the playlist itself. A collaborator adds, removes and
     * reorders; `mine` still decides renaming, the cover, the visibility
     * and the delete button. See the backend's `Collaborators`.
     */
    canEdit: query.data?.can_edit ?? query.data?.is_owner ?? false,
    followed: query.data?.followed ?? false,
    followable: query.data?.followable ?? false,
    /** Re-read this playlist and the lists it appears in. */
    refresh,
    ...usePlaylistFollow(playlistId),

    reorder: useMutation({
      mutationFn: (trackIds: number[]) => playlists.reorder(playlistId, trackIds),
      onSuccess: refresh,
    }),

    drop: useMutation({
      mutationFn: (trackId: number) => playlists.removeTrack(playlistId, trackId),
      onSuccess: refresh,
    }),

    rename: useMutation({
      mutationFn: (title: string) => playlists.rename(playlistId, title),
      onSuccess: refresh,
    }),

    setVisibility: useMutation({
      mutationFn: (next: Playlist['visibility']) => playlists.setVisibility(playlistId, next),
      onSuccess: refresh,
    }),

    destroy: useMutation({
      mutationFn: () => playlists.remove(playlistId),
      onSuccess: refresh,
    }),
  };
}

/**
 * Following, on its own so the drawer can share it with the page.
 *
 * Following puts a playlist in the library beside the reader's own lists,
 * and unfollowing takes it out - so the same two keys refresh as for every
 * edit, and the rail and the dock change with whichever surface pressed it.
 */
export function usePlaylistFollow(playlistId: number) {
  const client = useQueryClient();
  const user = useSession((state) => state.user);

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['playlist', playlistId] });
    void client.invalidateQueries({ queryKey: ['playlists', user?.username] });
  };

  return {
    follow: useMutation({
      mutationFn: () => playlists.follow(playlistId),
      onSuccess: refresh,
    }),
    unfollow: useMutation({
      mutationFn: () => playlists.unfollow(playlistId),
      onSuccess: refresh,
    }),
  };
}
